/**
 * Internationalization (i18n) Module
 *
 * Provides translation support for the application.
 * Currently only Spanish is supported.
 */

import { es, TranslationKeys } from './es'

// Available languages
export const languages = {
  es: 'Español',
} as const

export type Language = keyof typeof languages

// Default language
export const defaultLanguage: Language = 'es'

// Translation dictionaries
const translations: Record<Language, TranslationKeys> = {
  es,
}

// Get current language (could be expanded to use localStorage or context)
let currentLanguage: Language = defaultLanguage

export function setLanguage(lang: Language): void {
  currentLanguage = lang
}

export function getLanguage(): Language {
  return currentLanguage
}

/**
 * Get translation for a key path
 *
 * @example
 * t('common.save') // 'Guardar'
 * t('articles.title') // 'Artículos'
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const keys = key.split('.')
  let value: unknown = translations[currentLanguage]

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = (value as Record<string, unknown>)[k]
    } else {
      console.warn(`Translation missing: ${key}`)
      return key
    }
  }

  if (typeof value !== 'string') {
    console.warn(`Translation is not a string: ${key}`)
    return key
  }

  // Replace parameters like {min} or {max}
  if (params) {
    return value.replace(/\{(\w+)\}/g, (_, paramKey) => {
      return params[paramKey]?.toString() ?? `{${paramKey}}`
    })
  }

  return value
}

// Export types and translations
export type { TranslationKeys }
export { es }
